import { useRouter } from 'next/router'

import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import { GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'

import { CollectionFeatureProvider } from '../components/collection/collection-feature-provider'
import { CollectionPage } from '../components/collection/collection-page'
import Collection from '../components/collection/domains/Collection'
import { getCollectionCacheKey } from '../components/collection/utils'

import { PageTemplateProps } from './page-templates.types'

export interface CollectionPageData {
  readonly context: {
    readonly pageDataJson: Collection
    readonly seoData: GetSeoMetapageDataProps | null
  }
  readonly alternateUrls?: Record<string, string>
}

export default function CollectionPageTemplate({
  pageData,
  pathname,
  serverSideFeatureFlags,
  zipCode,
}: Readonly<PageTemplateProps>) {
  const router = useRouter()
  const searchParams = new URLSearchParams(router.query as Record<string, string>).toString()

  let pageDataJson = (pageData as unknown as CollectionPageData).context.pageDataJson
  const seoData = (pageData as unknown as CollectionPageData).context.seoData
  const alternateUrls = (pageData as unknown as CollectionPageData).alternateUrls
  const cacheKey = getCollectionCacheKey(pathname, searchParams)

  const { collectionPage: collectionPageCache } = collectionCacheStateManager.getCollectionCache(cacheKey) ?? {}

  if (collectionPageCache) {
    pageDataJson = collectionPageCache
  }

  // key={router.asPath} is used to force a re-render of the component when the pathname changes
  // As when we navigate to a new collection page, the component is rendered first with the old data of the previous collection page
  // and then with the new data of the new collection page
  // This is a workaround to avoid this issue
  // Adding Date.now() to the key is used to force a re-render of the component when the pathname does not change but the data changes
  // Jira ticket: https://lastbrand.atlassian.net/browse/STRFRNT-8974
  const collectionFeatureFlags =
    serverSideFeatureFlags?.featureFlagType === 'collection' ? serverSideFeatureFlags : null

  return (
    <CollectionFeatureProvider serverSideFeatureFlags={collectionFeatureFlags}>
      <CollectionPage
        collectionPage={pageDataJson}
        pathname={pathname}
        searchParams={searchParams}
        key={pathname}
        seoData={seoData}
        alternateUrls={alternateUrls}
        zipCode={zipCode}
      />
    </CollectionFeatureProvider>
  )
}
